import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useStore } from "../context/StoreContext";
import { useAuth } from "../context/AuthContext";
import api, { inr, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { FadeUp } from "../components/Reveal";
import { Tag, Lock, MailWarning } from "lucide-react";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Checkout() {
  const { cart, cartTotal, clearCart } = useStore();
  const { user, ready } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({ fullName: "", phone: "", address: "", city: "", state: "", pincode: "", email: "" });
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [resending, setResending] = useState(false);

  const needsVerify = !!user && user.role !== "admin" && !user.email_verified;

  const resendVerification = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success("Verification email sent. Please check your inbox.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not resend email.");
    } finally {
      setResending(false);
    }
  };

  useEffect(() => {
    if (ready && !user) {
      toast.error("Please sign in to checkout");
      navigate("/auth?redirect=/checkout");
    }
    if (user) setForm((f) => ({ ...f, fullName: user.name || "", email: user.email || "" }));
  }, [ready, user]);

  useEffect(() => {
    if (ready && cart.length === 0 && !processing) navigate("/shop");
  }, [ready, cart.length]);

  const discount = applied ? Math.round(cartTotal * applied.discount_percent / 100) : 0;
  const total = cartTotal - discount;

  const applyCoupon = async () => {
    try {
      const { data } = await api.post("/coupons/validate", { code: coupon, amount: cartTotal });
      setApplied(data);
      toast.success(`Coupon applied — ${data.discount_percent}% off`);
    } catch (err) {
      setApplied(null);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const placeOrder = async () => {
    if (needsVerify) {
      toast.error("Please verify your email before placing an order.");
      return;
    }
    if (!form.fullName || !form.phone || !form.address || !form.city || !form.pincode) {
      toast.error("Please complete your shipping details");
      return;
    }
    setProcessing(true);
    try {
      const items = cart.map((i) => ({
        product_id: i.product_id, name: i.name, price: i.price,
        size: i.size, quantity: i.quantity, image: i.image,
      }));
      const { data } = await api.post("/orders/create", {
        items, shipping: form, coupon_code: applied ? applied.code : null,
      });

      const finalize = async (payment = {}) => {
        await api.post("/orders/verify", { order_id: data.order_id, ...payment });
        clearCart();
        toast.success("Order confirmed");
        navigate(`/order/${data.order_id}`);
      };

      if (data.payment_enabled && data.razorpay_order) {
        const ok = await loadRazorpay();
        if (!ok) { toast.error("Could not load payment. Try again."); setProcessing(false); return; }
        const rzp = new window.Razorpay({
          key: data.razorpay_key_id,
          amount: data.razorpay_order.amount,
          currency: "INR",
          name: "Amraiya",
          description: "Couture order",
          order_id: data.razorpay_order.id,
          // Magic Checkout (1-click). Activates only if enabled on the Razorpay
          // account; otherwise Razorpay gracefully serves standard checkout.
          one_click_checkout: true,
          show_coupons: true,
          prefill: { name: form.fullName, email: form.email, contact: form.phone },
          theme: { color: "#1a1a1a" },
          handler: (resp) => finalize({
            razorpay_order_id: resp.razorpay_order_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
          }),
          modal: { ondismiss: () => setProcessing(false) },
        });
        rzp.open();
      } else {
        // Mock mode (no live keys) — confirm directly
        await finalize();
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setProcessing(false);
    }
  };

  return (
    <div className="pt-20" data-testid="checkout-page">
      <div className="px-5 md:px-10 pt-14 pb-8 max-w-[1300px] mx-auto">
        <FadeUp><p className="overline text-gold-deep mb-3">Secure Checkout</p></FadeUp>
        <h1 className="font-serif text-5xl md:text-7xl tracking-tight">Checkout</h1>
      </div>

      <div className="px-5 md:px-10 max-w-[1300px] mx-auto grid lg:grid-cols-[1fr_420px] gap-14 pb-28">
        {/* shipping */}
        <div>
          <h2 className="font-serif text-3xl mb-6">Shipping Details</h2>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Full Name" span2><Input value={form.fullName} onChange={set("fullName")} className="checkout-input" data-testid="ship-name" /></Field>
            <Field label="Phone"><Input value={form.phone} onChange={set("phone")} data-testid="ship-phone" /></Field>
            <Field label="Email"><Input value={form.email} onChange={set("email")} data-testid="ship-email" /></Field>
            <Field label="Address" span2><Input value={form.address} onChange={set("address")} data-testid="ship-address" /></Field>
            <Field label="City"><Input value={form.city} onChange={set("city")} data-testid="ship-city" /></Field>
            <Field label="State"><Input value={form.state} onChange={set("state")} data-testid="ship-state" /></Field>
            <Field label="Pincode"><Input value={form.pincode} onChange={set("pincode")} data-testid="ship-pincode" /></Field>
          </div>
        </div>

        {/* summary */}
        <div className="lg:sticky lg:top-28 self-start bg-[#f2efe9] p-8">
          <h2 className="font-serif text-3xl mb-6">Your Order</h2>
          <div className="space-y-4 max-h-64 overflow-y-auto mb-6">
            {cart.map((i) => (
              <div key={i.key} className="flex gap-3">
                <img src={i.image} alt={i.name} className="h-20 w-16 object-cover bg-[#efece6]" />
                <div className="flex-1 text-sm">
                  <p className="font-serif text-lg leading-tight">{i.name}</p>
                  <p className="text-ink-soft text-xs mt-1">Size {i.size} · Qty {i.quantity}</p>
                </div>
                <span className="text-sm">{inr(i.price * i.quantity)}</span>
              </div>
            ))}
          </div>

          {/* coupon */}
          <div className="flex gap-2 mb-5">
            <div className="flex items-center gap-2 flex-1 border border-line bg-ivory px-3">
              <Tag size={14} className="text-ink-soft" />
              <input value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="Promo code" className="flex-1 bg-transparent py-2.5 text-sm outline-none" data-testid="coupon-input" />
            </div>
            <button onClick={applyCoupon} className="btn-outline px-5 overline" data-testid="apply-coupon">Apply</button>
          </div>
          <p className="text-xs text-ink-soft mb-5">Try <span className="mono">AMRAIYA10__KEEP</span> for 10% off.</p>

          <div className="space-y-2 text-sm border-t border-line pt-5">
            <div className="flex justify-between"><span className="text-ink-soft">Subtotal</span><span>{inr(cartTotal)}</span></div>
            {discount > 0 && <div className="flex justify-between text-gold-deep"><span>Discount ({applied.discount_percent}%)</span><span>−{inr(discount)}</span></div>}
            <div className="flex justify-between"><span className="text-ink-soft">Shipping</span><span>Free</span></div>
          </div>
          <div className="flex justify-between py-5 border-t border-line mt-3">
            <span className="font-serif text-2xl">Total</span>
            <span className="font-serif text-2xl" data-testid="checkout-total">{inr(total)}</span>
          </div>

          <button onClick={placeOrder} disabled={processing || needsVerify} className="btn-ink w-full py-4 overline flex items-center justify-center gap-2 disabled:opacity-50" data-testid="place-order-btn">
            <Lock size={13} /> {processing ? "Processing…" : needsVerify ? "Verify email to pay" : `Pay ${inr(total)}`}
          </button>
          {needsVerify && (
            <div
              className="mt-4 border border-line px-4 py-3 flex items-start gap-3"
              style={{ background: "#fbf5ec" }}
              data-testid="checkout-verify-notice"
            >
              <MailWarning size={16} className="mt-0.5 shrink-0" style={{ color: "#a37a35" }} />
              <div className="flex-1 text-xs leading-relaxed">
                <p className="mb-1.5" style={{ color: "#5c4a44" }}>
                  Please verify <span className="mono">{user?.email}</span> before we can accept your order.
                </p>
                <button
                  onClick={resendVerification}
                  disabled={resending}
                  className="overline tracking-[0.22em] underline underline-offset-4 disabled:opacity-50"
                  style={{ color: "#a37a35" }}
                  data-testid="checkout-resend-verify"
                >
                  {resending ? "Sending…" : "Resend verification email"}
                </button>
              </div>
            </div>
          )}
          <p className="text-[0.65rem] text-ink-soft text-center mt-3">Payments secured via Razorpay</p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, span2 }) {
  return (
    <label className={`block ${span2 ? "col-span-2" : ""}`}>
      <span className="overline text-ink-soft mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
